import {
	type ProficiencyBandFill,
	getAnsweredCount,
	getAnsweredPoints,
	getFallbackProficiencyLevelLabel,
	groupQuestionTypesBySkill,
} from "@memorang/helpers/language-learning";
import {
	type MasteryWindow,
	QUESTION_TYPE_MASTERY_WINDOW,
	SKILL_MASTERY_WINDOW,
	buildMasteryBandFills,
	buildMasteryLevelSummaries,
	findScopeForValue,
} from "@memorang/helpers/language-learning-mastery";
import {
	compareTagSortOrder,
	isProficiencyTag,
	isQuestionTypeTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type {
	MixedItemDist,
	MixedItemTag,
	RecommendationContext,
	ReportDetails,
} from "@memorang/types";
import { z } from "zod";

export const LANGUAGE_LEARNING_HOME_SKILLS_USE_CASE =
	"language-learning.home-skills";
export const LANGUAGE_LEARNING_HOME_SUMMARY_USE_CASE =
	"language-learning.home-summary";
export const LANGUAGE_LEARNING_REPORT_USE_CASE = "language-learning.report";
export const LANGUAGE_LEARNING_PROGRESS_OVERALL_USE_CASE =
	"language-learning.progress-overall";
export const LANGUAGE_LEARNING_PROGRESS_SKILLS_USE_CASE =
	"language-learning.progress-skills";

const CANONICAL_SKILL_KEYS = [
	"reading",
	"listening",
	"speaking",
	"writing",
] as const;

export type CanonicalSkillKey = (typeof CANONICAL_SKILL_KEYS)[number];

const canonicalSkillKeySet = new Set<string>(CANONICAL_SKILL_KEYS);

const isCanonicalSkillKey = (value: string): value is CanonicalSkillKey =>
	canonicalSkillKeySet.has(value);

export const getCanonicalSkillKey = (
	tag: Pick<MixedItemTag, "title" | "code">,
): CanonicalSkillKey | undefined => {
	const candidates = [tag.code, tag.title];
	for (const candidate of candidates) {
		const normalized = candidate?.trim().toLowerCase();
		if (normalized && isCanonicalSkillKey(normalized)) {
			return normalized;
		}
	}
	return undefined;
};

export const getRecognizedSkillKeys = (
	skillTags: MixedItemTag[],
): CanonicalSkillKey[] => {
	const seen = new Set<CanonicalSkillKey>();
	const keys: CanonicalSkillKey[] = [];
	for (const tag of skillTags) {
		const key = getCanonicalSkillKey(tag);
		if (key && !seen.has(key)) {
			seen.add(key);
			keys.push(key);
		}
	}
	return keys;
};

const createSkillObjectSchema = <TValue extends z.ZodType>(
	keys: CanonicalSkillKey[],
	valueSchema: TValue,
) => {
	const shape: Record<string, TValue> = {};
	for (const key of keys) {
		shape[key] = valueSchema;
	}
	return z.object(shape).strict();
};

export const createHomeSkillsTipSchema = (keys: CanonicalSkillKey[]) =>
	z
		.object({
			skills: createSkillObjectSchema(
				keys,
				z
					.string()
					.describe(
						"One-sentence proficiency tip for this skill: the foundation at the current level and the next-level goal.",
					),
			),
		})
		.strict();

const progressSkillTipSchema = z
	.object({
		proficiency: z
			.string()
			.describe(
				"One-sentence level story for this skill: the foundation at the current level and the next-level goal.",
			),
		itemTypes: z
			.string()
			.describe(
				"One sentence naming this skill's strongest item type and the item type(s) to practice next.",
			),
	})
	.strict();

export const createProgressSkillsTipSchema = (keys: CanonicalSkillKey[]) =>
	z
		.object({
			skills: createSkillObjectSchema(keys, progressSkillTipSchema),
		})
		.strict();

export type ProgressSkillsTip = {
	skills: Partial<
		Record<CanonicalSkillKey, { proficiency: string; itemTypes: string }>
	>;
};

export const homeSummaryTipSchema = z
	.object({
		text: z
			.string()
			.describe(
				"One concise home summary connecting current proficiency with the next practice focus.",
			),
	})
	.strict();

export type HomeSummaryTip = z.infer<typeof homeSummaryTipSchema>;

export const reportTipSchema = z
	.object({
		session: z
			.string()
			.describe(
				"One sentence on how the session went — level and item-type strengths or weaknesses — plus the next focus.",
			),
		itemTypes: z
			.string()
			.describe(
				"One sentence naming which item types went well and which most need practice next.",
			),
	})
	.strict();
export type ReportTip = z.infer<typeof reportTipSchema>;

export const insightsReportTipSchema = z
	.object({ itemTypes: reportTipSchema.shape.itemTypes })
	.strict();
export type InsightsReportTip = z.infer<typeof insightsReportTipSchema>;

export type LanguageLearningReportTip = ReportTip | InsightsReportTip;

export const getReportSessionTip = (tip?: LanguageLearningReportTip): string =>
	tip && "session" in tip ? tip.session : "";

export const progressOverallTipSchema = z
	.object({
		text: z
			.string()
			.describe(
				"One sentence connecting how close the exam is with the skill that most affects exam readiness.",
			),
	})
	.strict();

type TagPracticeMetrics = {
	attemptedItems: number;
	availableItems: number;
	score?: number;
	averageScore?: number;
	numPointsEarned: number;
	numTotalPoints: number;
	numCorrect?: number;
	numIncorrect?: number;
	numSkipped?: number;
};

export type BandLevelSummary = {
	currentLevel?: string;
	nextLevel?: string;
	progress: number;
};

export type ItemTypeTipSummary = TagPracticeMetrics &
	BandLevelSummary & {
		title: string;
	};

export type ProficiencySummary = TagPracticeMetrics &
	BandLevelSummary & {
		key: CanonicalSkillKey;
		title: string;
		itemTypes: ItemTypeTipSummary[];
	};

export type ProficiencySummaries = Partial<
	Record<CanonicalSkillKey, ProficiencySummary>
>;

const summarizeTagPracticeMetrics = (tag: MixedItemTag): TagPracticeMetrics => {
	const points = getAnsweredPoints(tag);
	return {
		attemptedItems: getAnsweredCount(tag),
		availableItems: tag.numItems,
		score: tag.score,
		averageScore: tag.averageScore,
		numPointsEarned: tag.numPointsEarned ?? points.numPointsEarned,
		numTotalPoints: tag.numTotalPoints ?? points.numPointsTotal,
		numCorrect: tag.numCorrect,
		numIncorrect: tag.numIncorrect,
		numSkipped: tag.numSkipped,
	};
};

export type SkillBandFills = Map<MixedItemTag["id"], ProficiencyBandFill[]>;

// Rolling-mastery equivalent of the old points-qualification estimate:
// currentLevel is the highest mastered level, falling back to the lowest
// configured level while nothing is mastered yet (never "no level"). progress
// tracks how close the learner is to mastering the next unmastered level.
const buildMasteryEstimateSummary = (
	recommendationContext: RecommendationContext | undefined,
	proficiencyTags: MixedItemTag[],
	tagId: number,
	masteryWindow: MasteryWindow,
): BandLevelSummary => {
	const fallbackLevel = getFallbackProficiencyLevelLabel(proficiencyTags);
	const scope = recommendationContext
		? findScopeForValue(recommendationContext, tagId)
		: undefined;
	if (!recommendationContext || !scope) {
		return { currentLevel: fallbackLevel, progress: 0 };
	}
	const summaries = buildMasteryLevelSummaries({
		context: recommendationContext,
		scope,
		masteryWindow,
	});
	const fills = buildMasteryBandFills({
		context: recommendationContext,
		scope,
		masteryWindow,
	});
	let masteredIndex = -1;
	for (let i = summaries.length - 1; i >= 0; i--) {
		if (summaries[i]?.mastered) {
			masteredIndex = i;
			break;
		}
	}
	const currentLevel = summaries[masteredIndex]?.label ?? fallbackLevel;
	// The first unmastered level above the highest mastered one — never below
	// it, or a lower level the learner already surpassed could come back as
	// "next" (e.g. mastered index 2 of [unmastered, unmastered, mastered]).
	const nextIndex = summaries.findIndex(
		(summary, i) => i > masteredIndex && !summary.mastered,
	);
	if (nextIndex === -1) {
		return { currentLevel, progress: 100 };
	}
	return {
		currentLevel,
		nextLevel: summaries[nextIndex]?.label,
		progress: fills[nextIndex]?.percent ?? 0,
	};
};

const summarizeItemType = (
	tag: MixedItemTag,
	proficiencyTags: MixedItemTag[],
	recommendationContext: RecommendationContext | undefined,
): ItemTypeTipSummary => ({
	title: tag.title,
	...summarizeTagPracticeMetrics(tag),
	...buildMasteryEstimateSummary(
		recommendationContext,
		proficiencyTags,
		tag.id,
		QUESTION_TYPE_MASTERY_WINDOW,
	),
});

export const buildProficiencySummaries = (
	dist: MixedItemDist,
	recommendationContext: RecommendationContext | undefined,
): ProficiencySummaries => {
	const skillTags = dist.tags.filter(isSkillTag).sort(compareTagSortOrder);
	const proficiencyTags = dist.tags.filter(isProficiencyTag);
	const questionTypeTags = dist.tags.filter(isQuestionTypeTag);
	const rowsBySkill = groupQuestionTypesBySkill(skillTags, questionTypeTags);
	const summaries: ProficiencySummaries = {};

	for (const skill of skillTags) {
		const key = getCanonicalSkillKey(skill);
		if (!key) {
			continue;
		}
		summaries[key] = {
			key,
			title: skill.title,
			...summarizeTagPracticeMetrics(skill),
			...buildMasteryEstimateSummary(
				recommendationContext,
				proficiencyTags,
				skill.id,
				SKILL_MASTERY_WINDOW,
			),
			itemTypes: (rowsBySkill.get(skill.id) ?? []).map((itemTypeTag) =>
				summarizeItemType(itemTypeTag, proficiencyTags, recommendationContext),
			),
		};
	}

	return summaries;
};

/**
 * Extra prompt guidance sent with every language-learning tip request so the
 * generated text respects the evidence gate: a level may only be attributed
 * when the payload names it in `currentLevel`.
 */
export const LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE = [
	"Proficiency levels in the data are estimates from recent rolling",
	"performance: `currentLevel` is the learner's estimated level — the",
	"highest level they have mastered, or the lowest configured level by",
	"default while nothing is mastered yet. `nextLevel` is the level the",
	"learner is currently working to master (it can equal `currentLevel`",
	"while they work to master their starting level), and `progress`",
	"(0-100) is how close they are to mastering it. Levels can move down",
	"as well as up as recent performance changes — treat a lower level as",
	"a current snapshot, never as failure. `progress` values are provided",
	"for informational purposes only — never display the raw number or a",
	"percentage; normalize them into plain qualitative wording instead",
	'("just getting started", "well on your way", "almost there").',
].join(" ");

export const hasTipActivity = (summaries: ProficiencySummaries): boolean =>
	Object.values(summaries).some((summary) => summary.attemptedItems > 0);

// Recognized skills with zero attempts still carry default proficiency badges,
// which a tip request would otherwise rank as if they were real performance
// (PRD-8807). Filtering to attempted skills before both the payload and the
// schema keeps the model from being asked to name a "strongest" item type for
// a skill nobody has tried.
export const getAttemptedSkillKeys = (
	summaries: ProficiencySummaries,
	keys: CanonicalSkillKey[],
): CanonicalSkillKey[] =>
	keys.filter((key) => (summaries[key]?.attemptedItems ?? 0) > 0);

export const filterSummariesToKeys = (
	summaries: ProficiencySummaries,
	keys: CanonicalSkillKey[],
): ProficiencySummaries => {
	const filtered: ProficiencySummaries = {};
	for (const key of keys) {
		const summary = summaries[key];
		if (summary) {
			filtered[key] = summary;
		}
	}
	return filtered;
};

type LanguageLearningTipGate = {
	enabled: boolean;
	proficiencySummaries: ProficiencySummaries;
	recognizedSkillKeys: CanonicalSkillKey[];
};

const resolveLanguageLearningTipGate = ({
	isLanguageLearningApp,
	mixedItemDist,
	skillTags,
	recommendationContext,
}: {
	isLanguageLearningApp: boolean;
	mixedItemDist?: MixedItemDist;
	skillTags: MixedItemTag[];
	recommendationContext: RecommendationContext | undefined;
}): LanguageLearningTipGate => {
	if (!isLanguageLearningApp || !mixedItemDist) {
		return {
			enabled: false,
			proficiencySummaries: {},
			recognizedSkillKeys: [],
		};
	}
	const proficiencySummaries = buildProficiencySummaries(
		mixedItemDist,
		recommendationContext,
	);
	const recognizedSkillKeys = getRecognizedSkillKeys(skillTags);
	return {
		enabled:
			recognizedSkillKeys.length > 0 && hasTipActivity(proficiencySummaries),
		proficiencySummaries,
		recognizedSkillKeys,
	};
};

export const withTipAppFraming = <T extends object>(
	appId: string | number | undefined,
	template: string | undefined,
	data: T,
) => ({
	app: appId,
	template,
	...data,
});

const getProficiencyLevelLabels = (dist: MixedItemDist): string[] =>
	dist.tags
		.filter(isProficiencyTag)
		.sort(compareTagSortOrder)
		.map((tag) => tag.title);

export const buildSkillsTipData = (
	dist: MixedItemDist,
	skills: ProficiencySummaries,
) => ({
	exam: dist.title,
	levels: getProficiencyLevelLabels(dist),
	skills,
});

export type HomeSummaryExamFraming = {
	examDate: string;
	daysUntilExam: number;
};

export const buildHomeSummaryTipData = (
	dist: MixedItemDist,
	skills: ProficiencySummaries,
	examFraming?: HomeSummaryExamFraming,
) => ({
	...buildSkillsTipData(dist, skills),
	...(examFraming && examFraming.daysUntilExam >= 0 ? examFraming : {}),
});

export const buildProgressOverallTipData = (
	dist: MixedItemDist,
	skills: ProficiencySummaries,
	itemsAnswered: number,
	examFraming?: { examDate: string; daysUntilExam: number },
) => ({
	exam: dist.title,
	levels: getProficiencyLevelLabels(dist),
	itemsAnswered,
	skills,
	...(examFraming ?? {}),
});

const summarizeReportTag = (tag: MixedItemTag) => ({
	title: tag.title,
	numItems: tag.numItems,
	numCorrect: tag.numCorrect,
	numIncorrect: tag.numIncorrect,
	numSkipped: tag.numSkipped,
	score: tag.score,
	averageScore: tag.averageScore,
	numPointsEarned: tag.numPointsEarned,
	numTotalPoints: tag.numTotalPoints,
});

export const buildReportTipData = (report: ReportDetails) => ({
	examName: report.examName,
	sessionDate: report.sessionDate,
	reportSessionType: report.reportSessionType,
	timeElapsed: report.timeElapsed,
	numPointsEarned: report.numPointsEarned,
	numTotalPoints: report.numTotalPoints,
	numQuestions: report.numQuestions,
	numCorrect: report.numCorrect,
	numIncorrect: report.numIncorrect,
	numSkipped: report.numSkipped,
	topics: report.topics,
	skills: report.tagDetails.filter(isSkillTag).map(summarizeReportTag),
	itemTypes: report.tagDetails
		.filter(isQuestionTypeTag)
		.map(summarizeReportTag),
});

export type TipDataKeyArgs = {
	app: string;
	template: string;
	exam: string;
	language: string;
	skills: ProficiencySummaries;
	daysUntilExam?: number;
};

const buildTipCacheContext = ({
	appId,
	template,
	examId,
	examFallback,
	language,
	skills,
	daysUntilExam,
}: {
	appId?: string | number;
	template?: string;
	examId?: string | number;
	examFallback?: string;
	language: string;
	skills: ProficiencySummaries;
	daysUntilExam?: number;
}): TipDataKeyArgs => ({
	app: String(appId ?? ""),
	template: template ?? "",
	exam: String(examId ?? examFallback ?? ""),
	language,
	skills,
	...(daysUntilExam !== undefined ? { daysUntilExam } : {}),
});

export type TipSurfaceState =
	| { enabled: false }
	| {
			enabled: true;
			dist: MixedItemDist;
			proficiencySummaries: ProficiencySummaries;
			recognizedSkillKeys: CanonicalSkillKey[];
			cacheContext: TipDataKeyArgs;
	  };

export const buildTipSurfaceState = ({
	isLanguageLearningApp,
	mixedItemDist,
	skillTags,
	recommendationContext,
	appId,
	template,
	examId,
	language,
}: {
	isLanguageLearningApp: boolean;
	mixedItemDist?: MixedItemDist;
	skillTags: MixedItemTag[];
	recommendationContext: RecommendationContext | undefined;
	appId?: string | number;
	template?: string;
	examId?: string | number;
	language: string;
}): TipSurfaceState => {
	const { enabled, proficiencySummaries, recognizedSkillKeys } =
		resolveLanguageLearningTipGate({
			isLanguageLearningApp,
			mixedItemDist,
			skillTags,
			recommendationContext,
		});
	if (!enabled || !mixedItemDist) {
		return { enabled: false };
	}
	return {
		enabled: true,
		dist: mixedItemDist,
		proficiencySummaries,
		recognizedSkillKeys,
		cacheContext: buildTipCacheContext({
			appId,
			template,
			examId,
			examFallback: mixedItemDist.title,
			language,
			skills: proficiencySummaries,
		}),
	};
};

export const buildLanguageLearningTipDataKey = ({
	app,
	template,
	exam,
	language,
	skills,
	daysUntilExam,
}: TipDataKeyArgs): string =>
	JSON.stringify([
		app,
		template,
		exam,
		language,
		daysUntilExam ?? null,
		CANONICAL_SKILL_KEYS.flatMap((key) => {
			const skill = skills[key];
			return skill
				? [
						key,
						skill.attemptedItems,
						skill.currentLevel ?? null,
						skill.nextLevel ?? null,
						skill.progress,
						skill.score ?? null,
						skill.averageScore ?? null,
						skill.numPointsEarned,
						skill.numTotalPoints,
					]
				: [];
		}),
	]);
